import { useAdminSelection } from '../hook/adminSelectionHook.mjs';
import { useUsers } from '../hook/adminSelectionUsers.mjs';
import { useStats } from '../hook/adminSelectionStats.mjs';
import StatsCards from './adminSelectionStats.mjs';
import UsersTable from './adminUsersPage.mjs';
import Pagination from './adminPagination.mjs';
import { useState } from 'react';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';



const AdminSelectionDashboard = () => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { confirm } = useDialog();
  const { currentUser, loading: userLoading, error: userError } = useAdminSelection();
  const {
    users,
    pagination,
    loading: usersLoading,
    error: usersError,
    currentPage,
    loadUsers,
    searchUsers,
    promoteUser,
    demoteUser,
    setCurrentPage
  } = useUsers();


  const { stats, loading: statsLoading, refetch: refetchStats } = useStats();

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      searchUsers(query);
    } else if (query.length === 0) {
      loadUsers(1);
    }
  };

  const handleRoleFilter = (role) => {
    setRoleFilter(role);
    setSearchQuery('');
    if (role) {
      loadUsers(1, role);
    } else {
      loadUsers(1);
    }
  };

  const handlePromote = async (user) => {
    const confirmed = await confirm(
      `Are you sure you want to promote ${user.firstName} ${user.lastName} to admin?`
    );

    if (!confirmed) return;

    try {
      const response = await promoteUser(user._id);
      showMessage(response.message, 'success');
      refetchStats();
    } catch (error) {
      showMessage(error.message, 'error');
    }
  };

  const handleDemote = async (user) => {
    const confirmed = await confirm(
      `Are you sure you want to demote ${user.firstName} ${user.lastName} to regular user?`
    );

    if (!confirmed) return;

    try {
      const response = await demoteUser(user._id);
      showMessage(response.message, 'success');
      refetchStats();
    } catch (error) {
      showMessage(error.message, 'error');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadUsers(page);
  };

  if (userLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
        <p className="text-gray-600">Loading users data...</p>
      </div>
    );
  }

  if (userError) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {userError}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 px-4 pb-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Regional Admin Dashboard</h1>
          <p className="text-gray-500 mt-1">Manage users in your region</p>
          {currentUser?.region && (
            <div className="mt-3">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-stanford-red/10 text-stanford-red">
                <span className="w-2 h-2 rounded-full bg-stanford-red"></span>
                {currentUser.region}
              </span>
            </div>
          )}
        </div>

        {message.text && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {message.text}
          </div>
        )}

        <StatsCards stats={stats} loading={statsLoading} />

        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              type="text"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
              placeholder="Search by name, email, or position..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
              value={roleFilter}
              onChange={(e) => handleRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="user">Users Only</option>
              <option value="admin">Admins Only</option>
            </select>
          </div>
        </div>

        {usersError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {usersError}
          </div>
        )}

        <UsersTable
          users={users}
          loading={usersLoading}
          currentUser={currentUser}
          onPromote={handlePromote}
          onDemote={handleDemote}
        />

        <Pagination
          pagination={pagination}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default AdminSelectionDashboard;
